import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  listMaterials,
  createMaterial,
  uploadFileToS3,
  updateMaterial,
  deleteMaterial,
  getSubjects,
  getUploadUrlForUpdate,
  parseFilenameLocal,
  checkMaterialAccess,
  getMaterialViewUrl,
  getMaterialPrintUrl,
  listAllAnswerLinks,
  listAvailableAnswers,
  linkAnswer,
  YEARS,
  TERMS,
  WEEKS
} from '../../api/materialPortalApi';
import MaterialPdfViewerModal from '../../components/Portal/MaterialPdfViewerModal';
import MaterialAccessControlModal from '../../components/Portal/MaterialAccessControlModal';
import MaterialAnswerModal from '../../components/Portal/MaterialAnswerModal';
import { listCampuses } from '../../api/campusPortalApi';
import { canManageVisibilityRestrictions } from '../../utils/portalPermissions';
import * as S from './Management.styles';

// Lazy-loaded so only system_admin/developer browsers fetch this chunk.
const MaterialVisibilityEditor = lazy(() => import('../../components/Portal/MaterialVisibilityEditor'));

// Subject IDs for Year 5 English & Math merger
const ENGLISH_SUBJECT_ID = '30ed9f56-f541-4224-90f0-1fc84a277827';
const MATH_SUBJECT_ID = '410e5ab7-7062-4a06-b757-f4ce1e02cfa9';
const MERGED_SUBJECT_KEY = 'english-math-merged';

// Stable per-session id for rows in the upload form, so a row can reference
// another row ("link this question to that answer") even as rows are added or
// removed before the upload runs.
let uploadRowSeq = 0;
const nextUploadRowUid = () => `row-${++uploadRowSeq}`;

// "Y2T2W6 Eng ANS" / "...answers.pdf" — treat a trailing ANS/ANSWER token as an
// answer sheet, on top of the plain "answer" substring check.
const ANSWER_TOKEN_RE = /(^|[\s._-])(answers?|ans)([\s._-]|$)/i;
const isAnswerFilename = (filename) => {
  const base = filename.replace(/\.pdf$/i, '');
  return base.toLowerCase().includes('answer') || ANSWER_TOKEN_RE.test(base);
};

// File name reduced to a comparable key, with the answer marker taken out:
// "Y2T2W6 Eng ANS.pdf" and "Y2T2W6 Eng.pdf" both become "y2t2w6eng".
const answerPairKey = (filename) => filename
  .replace(/\.pdf$/i, '')
  .replace(/answers?/gi, ' ')
  .replace(/(^|[\s._-])ans([\s._-]|$)/gi, ' ')
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');

const MIN_PAIR_KEY_LENGTH = 3;

// Pre-select the obvious pairing: a question uploaded together with its own
// answer sheet ("Y2T2W6 Eng.pdf" + "Y2T2W6 Eng ANS.pdf").
//
// Year/term/week is NOT enough to pair on: one upload normally covers several
// subjects of the same week, and the subjects have not been picked yet at this
// point, so every question would grab whichever answer happened to come first.
// Pair on the file name instead, and only when exactly one answer matches —
// anything ambiguous is left for the user to choose.
const autoPairAnswers = (items) => {
  const answerKeys = items
    .filter(item => item.isAnswer)
    .map(item => ({ uid: item.uid, key: answerPairKey(item.file.name) }))
    .filter(entry => entry.key.length >= MIN_PAIR_KEY_LENGTH);

  return items.map(item => {
    if (item.isAnswer || item.linkAnswerRef) return item;

    const key = answerPairKey(item.file.name);
    if (key.length < MIN_PAIR_KEY_LENGTH) return item;

    // An exact match wins outright, so "Y3T1W2 Eng" takes "Y3T1W2 Eng ANS" even
    // when a week-wide "Y3T1W2 ANS" is in the same batch.
    const exact = answerKeys.filter(entry => entry.key === key);

    // Otherwise allow a suffix on either side: "Y2T2W6 Eng" pairs with
    // "Y2T2W6 English ANS", and "Y3T1W2 Eng Comprehension" with "Y3T1W2 ANS".
    const matches = exact.length > 0
      ? exact
      : answerKeys.filter(entry => entry.key.startsWith(key) || key.startsWith(entry.key));

    if (matches.length !== 1) return item;

    return { ...item, linkAnswerRef: `batch:${matches[0].uid}`, linkAutoMatched: true };
  });
};

// `batch:<uid>` points at another row in this upload (id known only after it is
// created); `existing:<id>` points at an answer already in the library.
const resolveAnswerRef = (ref, createdIds) => {
  if (!ref) return null;
  const separator = ref.indexOf(':');
  const kind = ref.slice(0, separator);
  const value = ref.slice(separator + 1);
  return kind === 'batch' ? (createdIds[value] || null) : value;
};

const MaterialsManagement = () => {
  const auth = useAuth();

  // Data states
  const [materials, setMaterials] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [answerLinks, setAnswerLinks] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [filterTerm, setFilterTerm] = useState('all');
  const [filterWeek, setFilterWeek] = useState('all');
  const [filterLinked, setFilterLinked] = useState('all'); // 'all' | 'linked' | 'unlinked'

  // Folder view states
  const [viewMode, setViewMode] = useState('flat'); // 'flat' or 'folder'
  const [folderPath, setFolderPath] = useState([]); // breadcrumb navigation path

  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // PDF Viewer states
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfViewerMode, setPdfViewerMode] = useState('view'); // 'view' or 'print'
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfMaterialName, setPdfMaterialName] = useState('');
  // 教材 id 传给查看器 → 手写笔记按「用户 × 教材」存到云端
  const [pdfMaterialId, setPdfMaterialId] = useState(null);
  const [, setMaterialAccessInfo] = useState({}); // { materialId: { canView, canPrint, viewCount, printCount, ... } }

  // Access Control Modal states
  const [showAccessControlModal, setShowAccessControlModal] = useState(false);
  const [accessControlMaterial, setAccessControlMaterial] = useState(null);

  // Answer Modal states
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [answerMaterial, setAnswerMaterial] = useState(null);

  // Upload form states
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [availableAnswers, setAvailableAnswers] = useState([]); // existing answers, for link-on-upload

  // Get user's Cognito groups
  const userGroups = auth.user?.profile['cognito:groups'] || [];
  const portalGroups = Array.isArray(userGroups)
    ? userGroups.filter(g => g.startsWith('portal_'))
    : typeof userGroups === 'string'
      ? userGroups.split(',').filter(g => g.trim().startsWith('portal_'))
      : [];

  // Permission checks
  const isDeveloper = portalGroups.includes('portal_developer');
  const isSystemAdmin = portalGroups.includes('portal_system_admin');
  const isDirector = portalGroups.includes('portal_director');
  const isManager = portalGroups.includes('portal_manager');
  const isAdmin = portalGroups.includes('portal_admin');
  const isTeacher = portalGroups.includes('portal_teacher');

  const canUpload = isDeveloper || isSystemAdmin || isDirector || isManager || isAdmin || isTeacher;
  const canManageVisibility = canManageVisibilityRestrictions(portalGroups);

  // Check if user can edit/delete a specific material
  // Admin roles can edit any material, teachers can only edit their own
  const canEditDeleteMaterial = (material) => {
    if (isDeveloper || isSystemAdmin || isDirector || isManager) return true;
    // Teachers will be able to edit/delete via UI, but backend enforces ownership
    // We show the buttons if they're a teacher (backend will reject if not owner)
    if (isTeacher) return true;
    return false;
  };

  // Check if user can see print button for a material
  // Teachers cannot print materials
  const canPrintMaterial = (material) => {
    if (isSystemAdmin || isDirector || isManager || isAdmin) return true;
    return false;
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Answer links are supplementary — a failure there must not blank the page.
      const fetches = [
        listMaterials(),
        getSubjects(),
        listAllAnswerLinks().catch(err => {
          console.error('Error fetching answer links:', err);
          return [];
        })
      ];
      if (canManageVisibility) {
        fetches.push(listCampuses());
      }
      const results = await Promise.all(fetches);
      setMaterials(results[0]);
      setSubjects(results[1]);
      setAnswerLinks(results[2] || []);
      if (canManageVisibility) {
        setCampuses(results[3] || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [canManageVisibility]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchData();
    }
  }, [auth.isAuthenticated, fetchData]);

  // Answers already in the library, so a file can be linked while uploading it
  useEffect(() => {
    if (!showUploadModal) return undefined;

    let cancelled = false;
    listAvailableAnswers()
      .then(answers => {
        if (!cancelled) setAvailableAnswers(answers);
      })
      .catch(err => console.error('Error fetching available answers:', err));

    return () => { cancelled = true; };
  }, [showUploadModal]);

  // Helper to flatten subjects for dropdowns
  const flattenSubjects = (subjectsList) => {
    const result = [];
    for (const parent of subjectsList) {
      result.push({ id: parent.id, subjectName: parent.subjectName, isParent: true });
      for (const child of (parent.children || [])) {
        result.push({ id: child.id, subjectName: child.subjectName, isParent: false, parentName: parent.subjectName });
      }
    }
    return result;
  };

  const flatSubjects = flattenSubjects(subjects);

  // Refresh just the answer links (cheap) — used after the Answers modal closes.
  const refreshAnswerLinks = useCallback(async () => {
    try {
      setAnswerLinks(await listAllAnswerLinks());
    } catch (err) {
      console.error('Error refreshing answer links:', err);
    }
  }, []);

  // materialId -> number of answers linked to it, answerId -> number of
  // materials it is used by. Lets every card say whether it is linked without
  // one request per material.
  const answerLinkCounts = useMemo(() => {
    const byMaterial = {};
    const byAnswer = {};
    answerLinks.forEach(link => {
      if (link.materialId) byMaterial[link.materialId] = (byMaterial[link.materialId] || 0) + 1;
      if (link.answerId) byAnswer[link.answerId] = (byAnswer[link.answerId] || 0) + 1;
    });
    return { byMaterial, byAnswer };
  }, [answerLinks]);

  // For a question: how many answers are linked to it.
  // For an answer: how many questions use it.
  const getLinkCount = useCallback((material) => (
    material.isAnswer
      ? (answerLinkCounts.byAnswer[material.id] || 0)
      : (answerLinkCounts.byMaterial[material.id] || 0)
  ), [answerLinkCounts]);

  // "Are they linked?" at a glance, on every material card
  const renderLinkBadge = (material) => {
    const count = getLinkCount(material);
    if (count > 0) {
      return (
        <S.Badge color="#10b981">
          {material.isAnswer ? `Used by ${count}` : `Answer Linked (${count})`}
        </S.Badge>
      );
    }
    return (
      <S.Badge color="#9ca3af">
        {material.isAnswer ? 'Not Used' : 'No Answer'}
      </S.Badge>
    );
  };

  // Filter materials (supports multi-subject with ANY match)
  const filteredMaterials = materials.filter(material => {
    // Search in name and all subject names
    const subjectNamesStr = (material.subjectNames || [material.subjectName]).filter(Boolean).join(' ');
    const matchesSearch = material.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         subjectNamesStr.toLowerCase().includes(searchTerm.toLowerCase());

    // Multi-subject filter: ANY match - check if filter subject is in material's subjects
    let matchesSubject = filterSubject === 'all';
    if (!matchesSubject) {
      const materialSubjectIds = material.subjectIds || (material.subjectId ? [material.subjectId] : []);
      matchesSubject = materialSubjectIds.includes(filterSubject);
    }

    const matchesYear = filterYear === 'all' || material.year === parseInt(filterYear);
    const matchesTerm = filterTerm === 'all' || material.term === parseInt(filterTerm);
    const matchesWeek = filterWeek === 'all' || material.week === parseInt(filterWeek);

    // Answer-link filter: "linked" = has at least one link (an answer counts as
    // linked when some material uses it)
    const matchesLinked = filterLinked === 'all' ||
      (filterLinked === 'linked' ? getLinkCount(material) > 0 : getLinkCount(material) === 0);

    return matchesSearch && matchesSubject && matchesYear && matchesTerm && matchesWeek && matchesLinked;
  });

  // ========== Folder View Helper Functions ==========

  // Get display label for year
  const getYearDisplayLabel = (year) => {
    if (year === -1) return 'Kindy';
    if (year === 0) return 'Pre-primary';
    if (year >= 1 && year <= 12) return `Year ${year}`;
    return 'Others';
  };

  // Get all subject keys for a material (supports multi-subject)
  // Returns an array of subject keys so material appears in all relevant folders
  const getSubjectKeys = (material) => {
    const subjectIds = material.subjectIds || (material.subjectId ? [material.subjectId] : []);

    // Year 5 special case: merge English & Math into one folder
    if (material.year === 5) {
      if (subjectIds.includes(ENGLISH_SUBJECT_ID) || subjectIds.includes(MATH_SUBJECT_ID)) {
        // Filter out English and Math, add merged key, keep other subjects
        const otherSubjects = subjectIds.filter(id => id !== ENGLISH_SUBJECT_ID && id !== MATH_SUBJECT_ID);
        return [MERGED_SUBJECT_KEY, ...otherSubjects];
      }
    }

    // Return all subject IDs, or 'others' if none
    return subjectIds.length > 0 ? subjectIds : ['others'];
  };

  // Get subject display label from subjectKey (subjectId)
  const getSubjectLabel = useCallback((subjectKey) => {
    if (subjectKey === MERGED_SUBJECT_KEY) {
      return 'English & Math';
    }
    if (subjectKey === 'others') {
      return 'Others';
    }
    // Look up subject name from flatSubjects array
    const subject = flatSubjects.find(s => s.id === subjectKey);
    return subject?.subjectName || 'Others';
  }, [flatSubjects]);

  // Get term display label
  const getTermDisplayLabel = (term) => {
    if (term === 0) return 'Special Term';
    if (term >= 1 && term <= 4) return `Term ${term}`;
    return 'Others';
  };

  // Get week display label
  const getWeekDisplayLabel = (week) => {
    if (week >= 1 && week <= 12) return `Week ${week}`;
    return 'Others';
  };

  // Count materials in a folder recursively
  const countMaterialsInFolder = useCallback((folder) => {
    const count = (f) => {
      if (f.materials) return f.materials.length;
      if (f.subfolders) {
        return Object.values(f.subfolders).reduce((sum, sub) => sum + count(sub), 0);
      }
      return 0;
    };
    return count(folder);
  }, []);

  // Build folder structure from filtered materials
  // Multi-subject materials appear in ALL matching subject folders
  const folderStructure = useMemo(() => {
    const structure = {};

    filteredMaterials.forEach(material => {
      // Year level
      const yearKey = (material.year !== null && material.year !== undefined && material.year >= -1 && material.year <= 12)
        ? String(material.year)
        : 'others';
      const yearLabel = getYearDisplayLabel(material.year);

      if (!structure[yearKey]) {
        structure[yearKey] = { label: yearLabel, value: material.year, subfolders: {} };
      }

      // Get all subject keys for this material (supports multi-subject)
      const subjectKeys = getSubjectKeys(material);

      // Add material to EACH subject folder it belongs to
      subjectKeys.forEach(subjectKey => {
        const subjectLabel = getSubjectLabel(subjectKey);

        if (!structure[yearKey].subfolders[subjectKey]) {
          structure[yearKey].subfolders[subjectKey] = { label: subjectLabel, value: subjectKey, subfolders: {} };
        }

        // Term level
        const termKey = (material.term !== null && material.term !== undefined && material.term >= 0 && material.term <= 4)
          ? String(material.term)
          : 'others';
        const termLabel = getTermDisplayLabel(material.term);

        if (!structure[yearKey].subfolders[subjectKey].subfolders[termKey]) {
          structure[yearKey].subfolders[subjectKey].subfolders[termKey] = { label: termLabel, value: material.term, subfolders: {} };
        }

        // Week level (skip for Special Term which has term=0)
        const termValue = material.term;
        const weekKey = (termValue === 0)
          ? 'no-week' // Special Term has no weeks, show materials directly
          : ((material.week !== null && material.week !== undefined && material.week >= 1 && material.week <= 12)
            ? String(material.week)
            : 'others');
        const weekLabel = (termValue === 0) ? 'Materials' : getWeekDisplayLabel(material.week);

        if (!structure[yearKey].subfolders[subjectKey].subfolders[termKey].subfolders[weekKey]) {
          structure[yearKey].subfolders[subjectKey].subfolders[termKey].subfolders[weekKey] = { label: weekLabel, value: material.week, materials: [] };
        }

        structure[yearKey].subfolders[subjectKey].subfolders[termKey].subfolders[weekKey].materials.push(material);
      });
    });

    return structure;
  }, [filteredMaterials, getSubjectLabel]);

  // Get current folder contents based on folderPath
  const currentFolderContents = useMemo(() => {
    if (folderPath.length === 0) {
      // Root level - show years
      return {
        type: 'folders',
        items: Object.entries(folderStructure).map(([key, data]) => ({
          key,
          label: data.label,
          value: data.value,
          level: 'year',
          count: countMaterialsInFolder(data)
        })).sort((a, b) => {
          // Sort: Kindy (-1), Pre-primary (0), Year 1-12, Others
          if (a.key === 'others') return 1;
          if (b.key === 'others') return -1;
          return parseInt(a.key) - parseInt(b.key);
        })
      };
    }

    // Navigate through the path
    let current = folderStructure;
    for (let i = 0; i < folderPath.length; i++) {
      const step = folderPath[i];
      if (step.level === 'year') {
        current = current[String(step.value)]?.subfolders || {};
      } else if (step.level === 'subject') {
        current = current[step.value]?.subfolders || {};
      } else if (step.level === 'term') {
        current = current[String(step.value)]?.subfolders || {};
      } else if (step.level === 'week') {
        // At week level, return materials
        const weekFolder = current[String(step.value)] || current['no-week'];
        return {
          type: 'materials',
          items: weekFolder?.materials || []
        };
      }
    }

    // Determine next level
    const currentLevel = folderPath[folderPath.length - 1]?.level;
    const nextLevel = {
      'year': 'subject',
      'subject': 'term',
      'term': 'week'
    }[currentLevel];

    if (nextLevel) {
      return {
        type: 'folders',
        items: Object.entries(current).map(([key, data]) => ({
          key,
          label: data.label,
          value: data.value !== undefined ? data.value : key,
          level: nextLevel,
          count: countMaterialsInFolder(data)
        })).sort((a, b) => {
          if (a.key === 'others' || a.key === 'no-week') return 1;
          if (b.key === 'others' || b.key === 'no-week') return -1;
          if (nextLevel === 'week' || nextLevel === 'term') {
            return parseInt(a.key) - parseInt(b.key);
          }
          return a.label.localeCompare(b.label);
        })
      };
    }

    return { type: 'materials', items: [] };
  }, [folderStructure, folderPath, countMaterialsInFolder]);

  // Navigation functions for folder view
  const navigateToFolder = (folderItem) => {
    setFolderPath(prev => [...prev, {
      level: folderItem.level,
      value: folderItem.value,
      label: folderItem.label
    }]);
  };

  const navigateToBreadcrumb = (index) => {
    if (index === -1) {
      setFolderPath([]);
    } else {
      setFolderPath(prev => prev.slice(0, index + 1));
    }
  };

  // ========== End Folder View Helper Functions ==========

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const pdfFiles = files.filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));

    if (pdfFiles.length !== files.length) {
      alert('Only PDF files are allowed');
    }

    // Parse filenames and create upload items
    const uploadItems = pdfFiles.map(file => {
      const parsed = parseFilenameLocal(file.name);
      return {
        uid: nextUploadRowUid(),
        file,
        name: file.name.replace('.pdf', ''),
        subjectIds: [], // Multi-subject support: array of subject IDs
        year: parsed.year || '',
        term: parsed.term || '',
        week: parsed.week || '',
        isAnswer: isAnswerFilename(file.name),
        linkAnswerRef: '', // '' | `batch:<uid>` | `existing:<answerId>`
        linkAutoMatched: false // true while the link is only a filename guess
      };
    });

    setUploadFiles(prev => autoPairAnswers([...prev, ...uploadItems]));
  };

  // Handle batch upload
  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      alert('Please select files to upload');
      return;
    }

    // Validate all files
    for (const item of uploadFiles) {
      // Special Term (0) doesn't require week
      const isSpecialTerm = parseInt(item.term) === 0;
      if (!item.subjectIds || item.subjectIds.length === 0 || item.year === '' || item.term === '' || (!isSpecialTerm && !item.week)) {
        alert(`Please fill all required fields for: ${item.file.name}`);
        return;
      }
    }

    setActionLoading(true);
    const progress = {};
    const createdIds = {}; // upload row uid -> created material id

    try {
      for (let i = 0; i < uploadFiles.length; i++) {
        const item = uploadFiles[i];
        progress[item.file.name] = 'uploading';
        setUploadProgress({ ...progress });

        // Create material record and get presigned URL
        const fileContentType = item.file.type || 'application/pdf';
        const termValue = parseInt(item.term);
        const { material, uploadUrl, contentType } = await createMaterial({
          name: item.name,
          subjectIds: item.subjectIds, // Multi-subject support
          year: parseInt(item.year),
          term: termValue,
          week: termValue === 0 ? null : parseInt(item.week), // Special Term doesn't have week
          isAnswer: item.isAnswer,
          filename: item.file.name,
          contentType: fileContentType
        });

        // Upload file to S3 with matching Content-Type
        await uploadFileToS3(uploadUrl, item.file, contentType || fileContentType);

        if (material?.id) {
          createdIds[item.uid] = material.id;
        }

        progress[item.file.name] = 'completed';
        setUploadProgress({ ...progress });
      }

      // Create the answer links requested in the form. Files are already
      // uploaded at this point, so a link failure is reported but never
      // discards the upload.
      const linkFailures = [];
      let linkedCount = 0;

      for (const item of uploadFiles) {
        if (item.isAnswer || !item.linkAnswerRef) continue;

        const materialId = createdIds[item.uid];
        const answerId = resolveAnswerRef(item.linkAnswerRef, createdIds);
        if (!materialId || !answerId) continue;

        try {
          await linkAnswer(materialId, answerId);
          linkedCount += 1;
        } catch (err) {
          console.error('Error linking answer:', err);
          linkFailures.push(`${item.name}: ${err.message}`);
        }
      }

      const linkSummary = linkedCount > 0 ? `\n${linkedCount} answer link(s) created.` : '';
      const failureSummary = linkFailures.length > 0
        ? `\n\nSome answers could not be linked:\n${linkFailures.join('\n')}`
        : '';
      alert(`All files uploaded successfully!${linkSummary}${failureSummary}`);
      setShowUploadModal(false);
      setUploadFiles([]);
      setUploadProgress({});
      fetchData();
    } catch (err) {
      console.error('Error uploading:', err);
      alert(`Upload failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (material) => {
    if (!window.confirm(`Are you sure you want to delete "${material.name}"?`)) {
      return;
    }

    setActionLoading(true);
    try {
      await deleteMaterial(material.id);
      setMaterials(materials.filter(m => m.id !== material.id));
      alert('Material deleted successfully');
    } catch (err) {
      console.error('Error deleting:', err);
      alert(`Delete failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle View - open PDF in view-only mode
  const handleView = async (material) => {
    setActionLoading(true);
    try {
      // Check access first
      const accessInfo = await checkMaterialAccess(material.id);
      setMaterialAccessInfo(prev => ({ ...prev, [material.id]: accessInfo }));

      if (!accessInfo.canView) {
        const limit = accessInfo.viewLimit || 3;
        alert(`You have reached the view limit (${limit} times) for this material.`);
        return;
      }

      // Get view URL (this records the access)
      const result = await getMaterialViewUrl(material.id);

      // Update access info with new count
      setMaterialAccessInfo(prev => ({
        ...prev,
        [material.id]: {
          ...prev[material.id],
          viewCount: (prev[material.id]?.viewCount || 0) + 1,
          canView: accessInfo.viewLimit === null || (accessInfo.viewCount + 1) < accessInfo.viewLimit
        }
      }));

      // Open PDF viewer in view mode
      setPdfUrl(result.url);
      setPdfMaterialName(material.name);
      setPdfMaterialId(material.id);
      setPdfViewerMode('view');
      setShowPdfViewer(true);

    } catch (err) {
      console.error('Error viewing material:', err);
      alert(`Failed to view material: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Print - open PDF in print mode
  const handlePrint = async (material) => {
    setActionLoading(true);
    try {
      // Check access first
      const accessInfo = await checkMaterialAccess(material.id);
      setMaterialAccessInfo(prev => ({ ...prev, [material.id]: accessInfo }));

      if (!accessInfo.canPrint) {
        if (accessInfo.printLimit === 0) {
          alert('Your role does not allow printing materials.');
        } else {
          const limit = accessInfo.printLimit || 3;
          alert(`You have reached the print limit (${limit} times) for this material.`);
        }
        return;
      }

      // Get print URL (this records the access)
      const result = await getMaterialPrintUrl(material.id);

      // Update access info with new count
      setMaterialAccessInfo(prev => ({
        ...prev,
        [material.id]: {
          ...prev[material.id],
          printCount: (prev[material.id]?.printCount || 0) + 1,
          canPrint: accessInfo.printLimit === null || (accessInfo.printCount + 1) < accessInfo.printLimit
        }
      }));

      // Open PDF viewer in print mode
      setPdfUrl(result.url);
      setPdfMaterialName(material.name);
      setPdfMaterialId(material.id);
      setPdfViewerMode('print');
      setShowPdfViewer(true);

    } catch (err) {
      console.error('Error printing material:', err);
      alert(`Failed to print material: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Close PDF viewer
  const closePdfViewer = () => {
    setShowPdfViewer(false);
    setPdfUrl(null);
    setPdfMaterialName('');
    setPdfMaterialId(null);
  };

  // Handle Access Control - open modal to manage extra access
  const handleAccessControl = (material) => {
    setAccessControlMaterial(material);
    setShowAccessControlModal(true);
  };

  // Close Access Control modal
  const closeAccessControlModal = () => {
    setShowAccessControlModal(false);
    setAccessControlMaterial(null);
  };

  // Handle Answers - open modal to view/link answers
  const handleAnswers = (material) => {
    setAnswerMaterial(material);
    setShowAnswerModal(true);
  };

  // Close Answer modal
  const closeAnswerModal = () => {
    setShowAnswerModal(false);
    setAnswerMaterial(null);
    refreshAnswerLinks(); // links may have changed inside the modal
  };

  // Handle edit
  const handleEdit = (material) => {
    setSelectedMaterial({ ...material });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (fileUpdate = null) => {
    if (!selectedMaterial) return;

    setActionLoading(true);
    try {
      const updateData = {
        name: selectedMaterial.name,
        subjectIds: selectedMaterial.subjectIds || (selectedMaterial.subjectId ? [selectedMaterial.subjectId] : []), // Multi-subject support
        year: selectedMaterial.year,
        term: selectedMaterial.term,
        week: selectedMaterial.term === 0 ? null : selectedMaterial.week, // Special Term doesn't have week
        isAnswer: selectedMaterial.isAnswer
      };

      // Only system_admin/developer may modify the campus visibility restriction.
      if (canManageVisibility) {
        updateData.visibleToCampuses = selectedMaterial.visibleToCampuses || [];
      }

      // Include new file path if provided
      if (fileUpdate && fileUpdate.path && fileUpdate.originalFilename) {
        updateData.path = fileUpdate.path;
        updateData.originalFilename = fileUpdate.originalFilename;
      }

      const updated = await updateMaterial(selectedMaterial.id, updateData);
      setMaterials(materials.map(m => m.id === updated.id ? updated : m));
      setShowEditModal(false);
      setSelectedMaterial(null);
      alert('Material updated successfully');
    } catch (err) {
      console.error('Error updating:', err);
      alert(`Update failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Loading state
  if (auth.isLoading || loading) {
    return (
      <S.Container>
        <S.Header>
          <div>
            <S.Title>Materials Management</S.Title>
            <S.Subtitle>Loading...</S.Subtitle>
          </div>
        </S.Header>
      </S.Container>
    );
  }

  return (
    <S.Container>
      <S.Header>
        <div>
          <S.Title>Materials Management</S.Title>
          <S.Subtitle>Manage teaching materials and PDFs</S.Subtitle>
        </div>
        <S.ButtonGroup>
          {canUpload && (
            <S.Button color="#10b981" onClick={() => setShowUploadModal(true)}>
              + Upload Materials
            </S.Button>
          )}
        </S.ButtonGroup>
      </S.Header>

      {error && (
        <S.ErrorBanner>
          Error loading materials: {error}
        </S.ErrorBanner>
      )}

      {/* Filters */}
      <S.FiltersBar>
        <S.SearchInput
          type="text"
          placeholder="Search materials..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <S.FilterSelect value={filterSubject} onChange={(e) => setFilterSubject(e.target.value)}>
          <option value="all">All Subjects</option>
          {flatSubjects.map(s => (
            <option key={s.id} value={s.id}>
              {s.isParent ? s.subjectName : `  - ${s.subjectName}`}
            </option>
          ))}
        </S.FilterSelect>
        <S.FilterSelect value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
          <option value="all">All Years</option>
          {YEARS.map(y => (
            <option key={y.value} value={y.value}>{y.label}</option>
          ))}
        </S.FilterSelect>
        <S.FilterSelect value={filterTerm} onChange={(e) => setFilterTerm(e.target.value)}>
          <option value="all">All Terms</option>
          {TERMS.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </S.FilterSelect>
        <S.FilterSelect value={filterWeek} onChange={(e) => setFilterWeek(e.target.value)}>
          <option value="all">All Weeks</option>
          {WEEKS.map(w => (
            <option key={w.value} value={w.value}>{w.label}</option>
          ))}
        </S.FilterSelect>
        <S.FilterSelect value={filterLinked} onChange={(e) => setFilterLinked(e.target.value)}>
          <option value="all">Linked &amp; Not Linked</option>
          <option value="linked">Linked Only</option>
          <option value="unlinked">Not Linked Only</option>
        </S.FilterSelect>
      </S.FiltersBar>

      {/* View Mode Toggle */}
      {(isDirector || isManager || isSystemAdmin) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
          <S.ViewToggleGroup>
            <S.ViewToggleButton
              $active={viewMode === 'flat'}
              onClick={() => {
                setViewMode('flat');
                setFolderPath([]);
              }}
            >
              Flat View
            </S.ViewToggleButton>
            <S.ViewToggleButton
              $active={viewMode === 'folder'}
              onClick={() => setViewMode('folder')}
            >
              Folder View
            </S.ViewToggleButton>
          </S.ViewToggleGroup>
        </div>
      )}

      {/* Breadcrumb Navigation - Only in Folder View */}
      {viewMode === 'folder' && (isDirector || isManager || isSystemAdmin) && (
        <S.Breadcrumb>
          <S.BreadcrumbItem
            $active={folderPath.length === 0}
            onClick={() => navigateToBreadcrumb(-1)}
          >
            All Materials
          </S.BreadcrumbItem>
          {folderPath.map((item, index) => (
            <React.Fragment key={index}>
              <S.BreadcrumbSeparator>/</S.BreadcrumbSeparator>
              <S.BreadcrumbItem
                $active={index === folderPath.length - 1}
                onClick={() => navigateToBreadcrumb(index)}
              >
                {item.label}
              </S.BreadcrumbItem>
            </React.Fragment>
          ))}
        </S.Breadcrumb>
      )}

      {/* Content Area */}
      {viewMode === 'folder' && (isDirector || isManager || isSystemAdmin) ? (
        // Folder View
        currentFolderContents.type === 'folders' ? (
          <S.FolderGrid>
            {currentFolderContents.items.length === 0 ? (
              <S.EmptyState>No folders found</S.EmptyState>
            ) : (
              currentFolderContents.items.map(folder => (
                <S.FolderCard key={folder.key} onClick={() => navigateToFolder(folder)}>
                  <S.FolderIcon />
                  <S.FolderLabel>{folder.label}</S.FolderLabel>
                  <S.FolderCount>{folder.count} material{folder.count !== 1 ? 's' : ''}</S.FolderCount>
                </S.FolderCard>
              ))
            )}
          </S.FolderGrid>
        ) : (
          // Materials inside a folder
          <S.MaterialsGrid>
            {currentFolderContents.items.length === 0 ? (
              <S.EmptyState>No materials in this folder</S.EmptyState>
            ) : (
              currentFolderContents.items.map(material => (
                <S.MaterialCard key={material.id}>
                  <S.MaterialIcon color={material.isAnswer ? '#f59e0b' : '#3b82f6'}>
                    {material.isAnswer ? 'A' : 'Q'}
                  </S.MaterialIcon>
                  <S.MaterialName>{material.name}</S.MaterialName>
                  <S.MaterialMeta>
                    {(material.subjectNames || [material.subjectName]).filter(Boolean).map((name, idx) => (
                      <S.Badge key={idx} color="#F1AF59">{name}</S.Badge>
                    ))}
                    {material.isAnswer && <S.Badge color="#f59e0b">Answer</S.Badge>}
                    {renderLinkBadge(material)}
                  </S.MaterialMeta>
                  <S.MaterialMeta>
                    {material.year === -1 ? 'Kindy' : material.year === 0 ? 'Pre-primary' : `Year ${material.year}`} | {material.term === 0 ? 'Special Term' : `Term ${material.term} | Week ${material.week}`}
                  </S.MaterialMeta>
                  <S.MaterialMeta>
                    Uploaded by {material.uploaderName || 'Unknown'}
                  </S.MaterialMeta>

                  <S.ActionButtons style={{ marginTop: '12px' }}>
                    <S.ActionButton
                      color="#3b82f6"
                      onClick={() => handleView(material)}
                      disabled={actionLoading}
                      title="View PDF"
                    >
                      View
                    </S.ActionButton>
                    {canPrintMaterial(material) && (
                      <S.ActionButton
                        color="#10b981"
                        onClick={() => handlePrint(material)}
                        disabled={actionLoading}
                        title="Print PDF"
                      >
                        Print
                      </S.ActionButton>
                    )}
                    {!material.isAnswer && (
                      <S.ActionButton
                        color="#f59e0b"
                        onClick={() => handleAnswers(material)}
                        title="Manage Linked Answers"
                      >
                        Answers
                      </S.ActionButton>
                    )}
                    {(isDirector || isManager || isSystemAdmin) && (
                      <S.ActionButton
                        color="#8b5cf6"
                        onClick={() => handleAccessControl(material)}
                        title="Manage Access"
                      >
                        Access
                      </S.ActionButton>
                    )}
                    {canEditDeleteMaterial(material) && (
                      <>
                        <S.ActionButton color="#F1AF59" onClick={() => handleEdit(material)}>
                          Edit
                        </S.ActionButton>
                        <S.ActionButton color="#ef4444" onClick={() => handleDelete(material)} disabled={actionLoading}>
                          Delete
                        </S.ActionButton>
                      </>
                    )}
                  </S.ActionButtons>
                </S.MaterialCard>
              ))
            )}
          </S.MaterialsGrid>
        )
      ) : (
        // Flat View (default)
        <S.MaterialsGrid>
        {filteredMaterials.length === 0 ? (
          <S.EmptyState>
            No materials found. {canUpload && 'Click "Upload Materials" to add your first material.'}
          </S.EmptyState>
        ) : (
          filteredMaterials.map(material => (
            <S.MaterialCard key={material.id}>
              <S.MaterialIcon color={material.isAnswer ? '#f59e0b' : '#3b82f6'}>
                {material.isAnswer ? 'A' : 'Q'}
              </S.MaterialIcon>
              <S.MaterialName>{material.name}</S.MaterialName>
              <S.MaterialMeta>
                {(material.subjectNames || [material.subjectName]).filter(Boolean).map((name, idx) => (
                  <S.Badge key={idx} color="#F1AF59">{name}</S.Badge>
                ))}
                {material.isAnswer && <S.Badge color="#f59e0b">Answer</S.Badge>}
                {renderLinkBadge(material)}
              </S.MaterialMeta>
              <S.MaterialMeta>
                {material.year === -1 ? 'Kindy' : material.year === 0 ? 'Pre-primary' : `Year ${material.year}`} | {material.term === 0 ? 'Special Term' : `Term ${material.term} | Week ${material.week}`}
              </S.MaterialMeta>
              <S.MaterialMeta>
                Uploaded by {material.uploaderName || 'Unknown'}
              </S.MaterialMeta>

              <S.ActionButtons style={{ marginTop: '12px' }}>
                <S.ActionButton
                  color="#3b82f6"
                  onClick={() => handleView(material)}
                  disabled={actionLoading}
                  title="View PDF"
                >
                  View
                </S.ActionButton>
                {/* Show Print button based on role and ownership */}
                {canPrintMaterial(material) && (
                  <S.ActionButton
                    color="#10b981"
                    onClick={() => handlePrint(material)}
                    disabled={actionLoading}
                    title="Print PDF"
                  >
                    Print
                  </S.ActionButton>
                )}
                {/* Answers button - only for non-answer materials */}
                {!material.isAnswer && (
                  <S.ActionButton
                    color="#f59e0b"
                    onClick={() => handleAnswers(material)}
                    title="Manage Linked Answers"
                  >
                    Answers
                  </S.ActionButton>
                )}
                {/* Access Control button for Director/Manager/SystemAdmin */}
                {(isDirector || isManager || isSystemAdmin) && (
                  <S.ActionButton
                    color="#8b5cf6"
                    onClick={() => handleAccessControl(material)}
                    title="Manage Access"
                  >
                    Access
                  </S.ActionButton>
                )}
                {canEditDeleteMaterial(material) && (
                  <>
                    <S.ActionButton color="#F1AF59" onClick={() => handleEdit(material)}>
                      Edit
                    </S.ActionButton>
                    <S.ActionButton color="#ef4444" onClick={() => handleDelete(material)} disabled={actionLoading}>
                      Delete
                    </S.ActionButton>
                  </>
                )}
              </S.ActionButtons>
            </S.MaterialCard>
          ))
        )}
      </S.MaterialsGrid>
      )}

      <S.Stats>
        {viewMode === 'folder' && (isDirector || isManager || isSystemAdmin) && currentFolderContents.type === 'materials'
          ? `Materials in folder: ${currentFolderContents.items.length}`
          : `Total Materials: ${filteredMaterials.length}`}
        {filterSubject !== 'all' || filterYear !== 'all' || filterTerm !== 'all' || filterWeek !== 'all' || filterLinked !== 'all'
          ? ` (filtered from ${materials.length})`
          : ''}
      </S.Stats>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          uploadFiles={uploadFiles}
          setUploadFiles={setUploadFiles}
          subjects={flatSubjects}
          availableAnswers={availableAnswers}
          uploadProgress={uploadProgress}
          actionLoading={actionLoading}
          onFileSelect={handleFileSelect}
          onUpload={handleUpload}
          onClose={() => {
            setShowUploadModal(false);
            setUploadFiles([]);
            setUploadProgress({});
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedMaterial && (
        <EditModal
          material={selectedMaterial}
          setMaterial={setSelectedMaterial}
          subjects={flatSubjects}
          campuses={campuses}
          canManageVisibility={canManageVisibility}
          actionLoading={actionLoading}
          onSave={handleSaveEdit}
          onClose={() => {
            setShowEditModal(false);
            setSelectedMaterial(null);
          }}
        />
      )}

      {/* PDF Viewer Modal */}
      <MaterialPdfViewerModal
        show={showPdfViewer}
        onClose={closePdfViewer}
        pdfUrl={pdfUrl}
        materialName={pdfMaterialName}
        materialId={pdfMaterialId}
        mode={pdfViewerMode}
      />

      {/* Access Control Modal */}
      <MaterialAccessControlModal
        show={showAccessControlModal}
        onClose={closeAccessControlModal}
        material={accessControlMaterial}
      />

      {/* Answer Modal */}
      <MaterialAnswerModal
        show={showAnswerModal}
        onClose={closeAnswerModal}
        material={answerMaterial}
      />
    </S.Container>
  );
};

// Upload Modal Component
const UploadModal = ({
  uploadFiles,
  setUploadFiles,
  subjects,
  availableAnswers = [],
  uploadProgress,
  actionLoading,
  onFileSelect,
  onUpload,
  onClose
}) => {
  const updateFile = (index, field, value) => {
    setUploadFiles(files => {
      const target = files[index];
      return files.map((f, i) => {
        if (i === index) {
          const updated = { ...f, [field]: value };
          // An answer sheet cannot itself link to an answer
          if (field === 'isAnswer' && value) updated.linkAnswerRef = '';
          // Once the user picks a link themselves it is no longer a guess
          if (field === 'isAnswer' || field === 'linkAnswerRef') updated.linkAutoMatched = false;
          return updated;
        }
        // A row that stops being an answer can no longer be a link target
        if (field === 'isAnswer' && !value && f.linkAnswerRef === `batch:${target.uid}`) {
          return { ...f, linkAnswerRef: '', linkAutoMatched: false };
        }
        return f;
      });
    });
  };

  const removeFile = (index) => {
    setUploadFiles(files => {
      const removedUid = files[index]?.uid;
      return files
        .filter((_, i) => i !== index)
        .map(f => (
          f.linkAnswerRef === `batch:${removedUid}`
            ? { ...f, linkAnswerRef: '', linkAutoMatched: false }
            : f
        ));
    });
  };

  const clearLink = (index) => updateFile(index, 'linkAnswerRef', '');

  // Auto-matched links are only a guess, so dropping all of them must be one
  // click — not one dropdown per row.
  const autoMatchedCount = uploadFiles.filter(f => f.linkAutoMatched && f.linkAnswerRef).length;

  const clearAutoMatchedLinks = () => {
    setUploadFiles(files => files.map(f => (
      f.linkAutoMatched ? { ...f, linkAnswerRef: '', linkAutoMatched: false } : f
    )));
  };

  // Answer sheets in this same upload, usable as link targets
  const batchAnswers = uploadFiles.filter(f => f.isAnswer);

  const subjectNamesOf = (answer) => {
    const ids = answer.subjectIds || (answer.subjectId ? [answer.subjectId] : []);
    return ids
      .map(id => subjects.find(s => s.id === id)?.subjectName)
      .filter(Boolean)
      .join(', ');
  };

  const formatAnswerOption = (answer) => {
    const yearLabel = answer.year === -1 ? 'Kindy' : answer.year === 0 ? 'Pre-primary' : `Y${answer.year}`;
    const termLabel = answer.term === 0 ? 'Special Term' : `T${answer.term}W${answer.week}`;
    const subjectLabel = subjectNamesOf(answer);
    return `${answer.name} (${yearLabel} ${termLabel}${subjectLabel ? ` - ${subjectLabel}` : ''})`;
  };

  return (
    <S.ModalOverlay>
      <S.Modal style={{ maxWidth: '800px' }}>
        <S.ModalHeader>
          <h3>Upload Materials</h3>
          <S.CloseButton onClick={onClose}>&times;</S.CloseButton>
        </S.ModalHeader>
        <S.ModalContent>
          <S.FormGroup>
            <label>Select PDF Files</label>
            <input
              type="file"
              accept=".pdf,application/pdf"
              multiple
              onChange={onFileSelect}
              style={{ marginBottom: '16px' }}
            />
          </S.FormGroup>

          {/* Auto-matched links are guesses — offer one click to drop them all */}
          {autoMatchedCount > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 12px',
              marginBottom: '16px',
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#92400e'
            }}>
              <span>
                {autoMatchedCount} answer link{autoMatchedCount > 1 ? 's were' : ' was'} auto-matched from file names.
              </span>
              <button
                type="button"
                onClick={clearAutoMatchedLinks}
                style={{
                  background: 'none',
                  border: '1px solid #d97706',
                  borderRadius: '4px',
                  padding: '4px 10px',
                  color: '#d97706',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                Clear all
              </button>
            </div>
          )}

          {/* File list */}
          {uploadFiles.map((item, index) => (
            <div key={index} style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <strong>{item.file.name}</strong>
                <button
                  onClick={() => removeFile(index)}
                  style={{ color: '#ef4444', cursor: 'pointer', background: 'none', border: 'none' }}
                >
                  Remove
                </button>
              </div>

              {uploadProgress[item.file.name] && (
                <S.Badge color={uploadProgress[item.file.name] === 'completed' ? '#10b981' : '#3b82f6'}>
                  {uploadProgress[item.file.name]}
                </S.Badge>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <S.FormGroup style={{ marginBottom: 0 }}>
                  <label>Display Name</label>
                  <S.Input
                    value={item.name}
                    onChange={(e) => updateFile(index, 'name', e.target.value)}
                  />
                </S.FormGroup>
                <S.FormGroup style={{ marginBottom: 0 }}>
                  <label>Subject(s) * <span style={{ fontSize: '12px', color: '#6b7280' }}>(select one or more)</span></label>
                  <div style={{
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    padding: '8px',
                    maxHeight: '120px',
                    overflowY: 'auto',
                    background: 'white'
                  }}>
                    {subjects.map(s => (
                      <label
                        key={s.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '4px',
                          cursor: 'pointer',
                          paddingLeft: s.isParent ? '4px' : '20px'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={(item.subjectIds || []).includes(s.id)}
                          onChange={(e) => {
                            const currentIds = item.subjectIds || [];
                            if (e.target.checked) {
                              updateFile(index, 'subjectIds', [...currentIds, s.id]);
                            } else {
                              updateFile(index, 'subjectIds', currentIds.filter(id => id !== s.id));
                            }
                          }}
                        />
                        <span style={{ fontWeight: s.isParent ? '500' : '400' }}>
                          {s.isParent ? s.subjectName : `${s.subjectName}`}
                        </span>
                      </label>
                    ))}
                  </div>
                </S.FormGroup>
                <S.FormGroup style={{ marginBottom: 0 }}>
                  <label>Year *</label>
                  <S.FilterSelect
                    value={item.year}
                    onChange={(e) => updateFile(index, 'year', e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="">Select Year</option>
                    {YEARS.map(y => (
                      <option key={y.value} value={y.value}>{y.label}</option>
                    ))}
                  </S.FilterSelect>
                </S.FormGroup>
                <S.FormGroup style={{ marginBottom: 0 }}>
                  <label>Term *</label>
                  <S.FilterSelect
                    value={item.term}
                    onChange={(e) => updateFile(index, 'term', e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="">Select Term</option>
                    {TERMS.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </S.FilterSelect>
                </S.FormGroup>
                {/* Week field - hidden for Special Term (0) */}
                {parseInt(item.term) !== 0 && (
                  <S.FormGroup style={{ marginBottom: 0 }}>
                    <label>Week *</label>
                    <S.FilterSelect
                      value={item.week}
                      onChange={(e) => updateFile(index, 'week', e.target.value)}
                      style={{ width: '100%' }}
                    >
                      <option value="">Select Week</option>
                      {WEEKS.map(w => (
                        <option key={w.value} value={w.value}>{w.label}</option>
                      ))}
                    </S.FilterSelect>
                  </S.FormGroup>
                )}
                <S.FormGroup style={{ marginBottom: 0, display: 'flex', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={item.isAnswer}
                      onChange={(e) => updateFile(index, 'isAnswer', e.target.checked)}
                    />
                    This is an answer sheet
                  </label>
                </S.FormGroup>
                {/* Link an answer straight away, instead of via the Answers modal after upload */}
                {!item.isAnswer && (
                  <S.FormGroup style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                    <label>
                      Link Answer <span style={{ fontSize: '12px', color: '#6b7280' }}>(optional)</span>
                    </label>
                    <S.FilterSelect
                      value={item.linkAnswerRef || ''}
                      onChange={(e) => updateFile(index, 'linkAnswerRef', e.target.value)}
                      style={{ width: '100%' }}
                    >
                      <option value="">No answer linked</option>
                      {batchAnswers.length > 0 && (
                        <optgroup label="From this upload">
                          {batchAnswers.map(answer => (
                            <option key={answer.uid} value={`batch:${answer.uid}`}>
                              {answer.name || answer.file.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {availableAnswers.length > 0 && (
                        <optgroup label="Existing answers">
                          {availableAnswers.map(answer => (
                            <option key={answer.id} value={`existing:${answer.id}`}>
                              {formatAnswerOption(answer)}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </S.FilterSelect>
                    {item.linkAnswerRef && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                        {item.linkAutoMatched && (
                          <span style={{ color: '#d97706' }}>
                            Auto-matched from the file name — please check.
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => clearLink(index)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: '#6b7280',
                            cursor: 'pointer',
                            textDecoration: 'underline'
                          }}
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </S.FormGroup>
                )}
              </div>
            </div>
          ))}

          {uploadFiles.length === 0 && (
            <S.Note>
              Select PDF files to upload. File names containing patterns like Y3T2W1 will be auto-parsed.
              Files whose names end with ANS / ANSWER are marked as answer sheets, and a question uploaded
              alongside its answer for the same year/term/week is linked automatically.
            </S.Note>
          )}
        </S.ModalContent>
        <S.ModalFooter>
          <S.Button color="#6b7280" onClick={onClose}>
            Cancel
          </S.Button>
          <S.Button
            color="#10b981"
            onClick={onUpload}
            disabled={actionLoading || uploadFiles.length === 0}
          >
            {actionLoading ? 'Uploading...' : `Upload ${uploadFiles.length} File(s)`}
          </S.Button>
        </S.ModalFooter>
      </S.Modal>
    </S.ModalOverlay>
  );
};

// Edit Modal Component
const EditModal = ({
  material,
  setMaterial,
  subjects,
  campuses = [],
  canManageVisibility = false,
  actionLoading,
  onSave,
  onClose
}) => {
  const [newFile, setNewFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(''); // '', 'uploading', 'uploaded'

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf' && !file.name.endsWith('.pdf')) {
        alert('Only PDF files are allowed');
        return;
      }
      setNewFile(file);
      setUploadStatus('');
    }
  };

  const handleSaveWithFile = async () => {
    if (newFile) {
      try {
        setUploadStatus('uploading');

        // Get presigned URL for the new file
        const contentType = newFile.type || 'application/pdf';
        const { uploadUrl, s3Key } = await getUploadUrlForUpdate(
          material.id,
          newFile.name,
          contentType
        );

        // Upload file to S3
        await uploadFileToS3(uploadUrl, newFile, contentType);
        setUploadStatus('uploaded');

        // Update material with new path and originalFilename
        setMaterial(prev => ({
          ...prev,
          path: s3Key,
          originalFilename: newFile.name
        }));

        // Call onSave with path info included
        onSave({ path: s3Key, originalFilename: newFile.name });
      } catch (err) {
        console.error('Error uploading new file:', err);
        alert(`File upload failed: ${err.message}`);
        setUploadStatus('');
      }
    } else {
      // No new file, just save metadata
      onSave();
    }
  };

  return (
    <S.ModalOverlay>
      <S.Modal>
        <S.ModalHeader>
          <h3>Edit Material</h3>
          <S.CloseButton onClick={onClose}>&times;</S.CloseButton>
        </S.ModalHeader>
        <S.ModalContent>
          <S.FormGroup>
            <label>Display Name</label>
            <S.Input
              value={material.name}
              onChange={(e) => setMaterial({ ...material, name: e.target.value })}
            />
          </S.FormGroup>
          <S.FormGroup>
            <label>Subject(s) <span style={{ fontSize: '12px', color: '#6b7280' }}>(select one or more)</span></label>
            <div style={{
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              padding: '8px',
              maxHeight: '150px',
              overflowY: 'auto',
              background: 'white'
            }}>
              {subjects.map(s => {
                // Get current selected IDs (support both old and new format)
                const currentIds = material.subjectIds || (material.subjectId ? [material.subjectId] : []);
                const isChecked = currentIds.includes(s.id);
                return (
                  <label
                    key={s.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '4px',
                      cursor: 'pointer',
                      paddingLeft: s.isParent ? '4px' : '20px'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        const prevIds = material.subjectIds || (material.subjectId ? [material.subjectId] : []);
                        if (e.target.checked) {
                          setMaterial({ ...material, subjectIds: [...prevIds, s.id] });
                        } else {
                          setMaterial({ ...material, subjectIds: prevIds.filter(id => id !== s.id) });
                        }
                      }}
                    />
                    <span style={{ fontWeight: s.isParent ? '500' : '400' }}>
                      {s.isParent ? s.subjectName : `${s.subjectName}`}
                    </span>
                  </label>
                );
              })}
            </div>
          </S.FormGroup>
          <S.FormGroup>
            <label>Year</label>
            <S.FilterSelect
              value={material.year}
              onChange={(e) => setMaterial({ ...material, year: parseInt(e.target.value) })}
              style={{ width: '100%' }}
            >
              {YEARS.map(y => (
                <option key={y.value} value={y.value}>{y.label}</option>
              ))}
            </S.FilterSelect>
          </S.FormGroup>
          <S.FormGroup>
            <label>Term</label>
            <S.FilterSelect
              value={material.term}
              onChange={(e) => setMaterial({ ...material, term: parseInt(e.target.value) })}
              style={{ width: '100%' }}
            >
              {TERMS.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </S.FilterSelect>
          </S.FormGroup>
          {/* Week field - hidden for Special Term (0) */}
          {material.term !== 0 && (
            <S.FormGroup>
              <label>Week</label>
              <S.FilterSelect
                value={material.week}
                onChange={(e) => setMaterial({ ...material, week: parseInt(e.target.value) })}
                style={{ width: '100%' }}
              >
                {WEEKS.map(w => (
                  <option key={w.value} value={w.value}>{w.label}</option>
                ))}
              </S.FilterSelect>
            </S.FormGroup>
          )}
          <S.FormGroup>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={material.isAnswer}
                onChange={(e) => setMaterial({ ...material, isAnswer: e.target.checked })}
              />
              This is an answer sheet
            </label>
          </S.FormGroup>

          {canManageVisibility && (
            <Suspense fallback={null}>
              <MaterialVisibilityEditor
                material={material}
                setMaterial={setMaterial}
                campuses={campuses}
              />
            </Suspense>
          )}

          {/* File re-upload section */}
          <S.FormGroup style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
            <label>Replace File (optional)</label>
            <S.Note style={{ marginBottom: '8px' }}>
              Current file: {material.originalFilename || 'Unknown'}
            </S.Note>
            <input
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
            />
            {newFile && (
              <S.Note style={{ marginTop: '8px', color: '#10b981' }}>
                New file selected: {newFile.name}
                {uploadStatus === 'uploading' && ' (Uploading...)'}
                {uploadStatus === 'uploaded' && ' (Uploaded)'}
              </S.Note>
            )}
          </S.FormGroup>
        </S.ModalContent>
        <S.ModalFooter>
          <S.Button color="#6b7280" onClick={onClose}>
            Cancel
          </S.Button>
          <S.Button
            color="#F1AF59"
            onClick={handleSaveWithFile}
            disabled={actionLoading || uploadStatus === 'uploading'}
          >
            {actionLoading || uploadStatus === 'uploading' ? 'Saving...' : 'Save Changes'}
          </S.Button>
        </S.ModalFooter>
      </S.Modal>
    </S.ModalOverlay>
  );
};

export default MaterialsManagement;
